// UI-level session state shared across screens: selected branch, operator,
// date range, gross/net mode. Persisted to localStorage so it survives reloads.
// Domain auth state (cinfo/uinfo/apir) stays in GlobalContext.

import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';

const STORAGE_KEY = 'sa.app-state.v1';

const defaults = {
  branch: 'ALL',
  operator: 'ALL',
  date: 'today',      // today | yesterday | week | month
  mode: 'gross',      // gross | net
  view: 'operator',   // operator | branch  (dashboard scope toggle)
};

const AppStateContext = createContext(null);

const load = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaults;
    return { ...defaults, ...JSON.parse(raw) };
  } catch {
    return defaults;
  }
};

export const AppStateProvider = ({ children }) => {
  const [state, setState] = useState(load);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {}
  }, [state]);

  const value = useMemo(() => ({
    ...state,
    setBranch:   (v) => setState(s => ({ ...s, branch: v })),
    setOperator: (v) => setState(s => ({ ...s, operator: v })),
    setDate:     (v) => setState(s => ({ ...s, date: v })),
    setMode:     (v) => setState(s => ({ ...s, mode: v })),
    setView:     (v) => setState(s => ({ ...s, view: v })),
    reset:       () => setState(defaults),
  }), [state]);

  return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>;
};

export const useAppState = () => {
  const ctx = useContext(AppStateContext);
  if (!ctx) throw new Error('useAppState must be used inside <AppStateProvider>');
  return ctx;
};
